import PropTypes from 'prop-types'
import { useCallback, useContext, useEffect, useState } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'

import { IconDolar } from '@loadsmart/icons'
import { BannerActionLarge } from '@loadsmart/loadsmart-ui'

import LoadingV2 from 'legacy/components/LoadingV2/LoadingV2'
import { LoadBoardContext } from 'legacy/contexts/loadBoard'
import analytics from 'legacy/utils/analytics'
import { differenceInMilliseconds } from 'legacy/utils/date/differenceInMilliseconds'
import { formatDate } from 'legacy/utils/date/formatDate'
import sentry from 'legacy/utils/sentry'

import { useConfigContext } from 'config/ui/ConfigContext'
import { BookingType } from 'loads/domain/LoadOffer'
import { useBookingOffer } from 'loads/ui/hooks/useBookingOffer'

import { useBidDialog } from '../BidManager/BidDialog/useBidDialog'
import { CONTEXTS } from '../OfferCard/OfferCard'
import { getBidDescription } from '../utils/getBidDescription'
import { Notification } from './Notification'

function getTimeout(expiresAt) {
  const creationDate = new Date()
  const expirationDate = new Date(expiresAt)
  const diff = differenceInMilliseconds(expirationDate, creationDate)
  return formatDate(diff, 'mm:ss')
}

function BidAwardedNotification({ bid }) {
  const history = useHistory()
  const { search, pathname } = useLocation()
  const { navigateToBookOffer } = useBookingOffer()
  const { refuseBid } = useBidDialog()

  const [
    {
      carrier: { ready: isCarrierReady },
    },
  ] = useContext(LoadBoardContext)
  const [accepting, setAccepting] = useState(false)
  const {
    load: { uuid: loadUUID, stops },
    expires_at,
    price,
    source,
  } = bid

  const showOnboardingImprovements = useConfigContext().showOnboardingImprovements.toBoolean()

  const bidDescription = getBidDescription(stops, price)
  const isCounterOffer = Boolean(bid.countered_bid)
  const isFromAlice = bid.source === 'internal'

  const handleDismiss = useCallback(() => {
    analytics.track('Bid Awarded Banner / Dismiss', {
      isCounterOffer,
      source,
      loadMode: bid.load.mode,
    })

    refuseBid(bid)
  }, [isCounterOffer, source, bid, refuseBid])

  const handleAcceptBidClick = useCallback(
    bookingType => async () => {
      if (showOnboardingImprovements && !isCarrierReady) {
        history.push(`${pathname}?${search}&finishOnboarding=1`)
        return
      }

      setAccepting(true)
      try {
        navigateToBookOffer({
          id: loadUUID,
          position: bid.load.position,
          triggerContext: CONTEXTS.LOAD_BOARD,
          bookingType,
        })

        analytics.track('Bid Awarded Banner / Accept Offer', {
          isCounterOffer,
          source,
          loadMode: bid.load.mode,
        })
        analytics.tag(['Bid Awarded'])
        setAccepting(false)
      } catch (error) {
        toast.error('Something went wrong when accepting the offer. Please try again.')
        sentry.log(error)
        analytics.tag(['Bid Awarded Accept Error'])
      }
      setAccepting(false)
    },
    [
      showOnboardingImprovements,
      isCarrierReady,
      history,
      pathname,
      search,
      navigateToBookOffer,
      loadUUID,
      bid.load.position,
      bid.load.mode,
      isCounterOffer,
      source,
    ]
  )

  useEffect(() => {
    analytics.track('Bid Awarded Banner / View', {
      isCounterOffer,
      source,
      loadMode: bid.load.mode,
    })
  }, [isCounterOffer, source, bid.load])

  const [time, setTime] = useState(getTimeout(expires_at))

  useEffect(() => {
    const interval = setInterval(() => {
      setTime(getTimeout(expires_at))
    }, 1000)

    return () => clearInterval(interval)
  })

  const actionLabel = accepting ? <LoadingV2 width={16} height={16} /> : `Accept Offer ${time}`

  if (isCounterOffer && isFromAlice) {
    return (
      <Notification>
        <BannerActionLarge
          title={`You received a counter offer for ${bidDescription}`}
          variant="warning"
          icon={<IconDolar width="24" height="24" />}
          action={actionLabel}
          onActionButtonClick={handleAcceptBidClick(BookingType.BidCounter)}
          onClose={handleDismiss}
          dismissible
        />
      </Notification>
    )
  }

  return (
    <Notification>
      <BannerActionLarge
        title={`Your bid offer for ${bidDescription} was awarded.`}
        variant="success"
        action={actionLabel}
        onActionButtonClick={handleAcceptBidClick(BookingType.BidAward)}
        secondaryAction={'Refuse'}
        onSecondaryActionButtonClick={handleDismiss}
        onClose={handleDismiss}
        dismissible
      />
    </Notification>
  )
}

BidAwardedNotification.propTypes = {
  bid: PropTypes.shape({}).isRequired,
}

export { BidAwardedNotification }
